import type { FC } from 'react';
import React, { memo, useCallback } from 'react';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FlagsProvider } from '@atlaskit/flag';

import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon.ts';
import { SpaceStarButton } from '@confluence/action-buttons/entry-points/spaceStar.ts';
import { SpaceWatchButton } from '@confluence/action-buttons/entry-points/spaceWatch.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { SpaceProp, ResponsiveCategoriesProps } from '../types.ts';

import { Categories } from './Categories.tsx';
import { Menu } from './Menu.tsx';
import {
	Row,
	Title,
	SpaceLink,
	IconContainer,
	IconPlaceholder,
	SpaceItem,
} from './PresentationalComponents.tsx';

export const DEFAULT_ICON_PATH = '/images/logo/default-space-logo-256.png';

const hideAtNarrowWidths = css({
	display: 'contents',
	'@media only screen and (max-width: 500px)': {
		display: 'none',
	},
});

type AnalyticsProps = {
	ranking: number;
	pageNumber: number;
	textFilter: boolean;
};

type TableRowProps = SpaceProp & AnalyticsProps & ResponsiveCategoriesProps;

export const TableRow: FC<TableRowProps> = memo(
	({ space, ranking, pageNumber, textFilter, categoriesWidth, setCategoriesWidth }) => {
		const { isLicensed } = useSessionData();
		const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();
		const iconPath = `${space.icon?.path || DEFAULT_ICON_PATH}`;

		const { createAnalyticsEvent } = useAnalyticsEvents();

		const onClick = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'spaceLink',
					source: 'spaceDirectoryScreen',
					attributes: {
						spaceId: space.id,
						ranking,
						pageNumber,
						textFilter,
						isGuest,
					},
				},
			}).fire();
		}, [space.id, ranking, pageNumber, textFilter, createAnalyticsEvent, isGuest]);

		const analytics = {
			source: 'spaceDirectoryScreen',
			attributes: {
				spaceId: space.id,
				isGuest,
			},
		};

		const SpaceItemContent = () => (
			<>
				<SpaceIcon src={iconPath} testId="space-icon" label="" />
				<Title title={space.name || ''}>{space.name}</Title>
				<Categories
					space={space}
					categoriesWidth={categoriesWidth}
					setCategoriesWidth={setCategoriesWidth}
				/>
				<IconPlaceholder />
			</>
		);

		return (
			<FlagsProvider>
				<Row>
					<SpaceItem>
						<SpaceLink
							href={SPACE_OVERVIEW.toUrl({
								spaceKey: space.alias || space.key,
							})}
							onClick={onClick}
							tabIndex={0}
						>
							<SpaceItemContent />
						</SpaceLink>
					</SpaceItem>
					{isLicensed ? (
						<IconContainer>
							<div css={hideAtNarrowWidths}>
								<SpaceWatchButton
									spaceId={space.id!}
									isWatched={!!space.currentUser?.isWatched}
									analytics={analytics}
								/>
								<SpaceStarButton
									spaceId={space.id!}
									spaceKey={space.key!}
									isStarred={!!space.currentUser?.isFavourited}
									analytics={analytics}
									spacing="none"
								/>
							</div>
							<Menu space={space} />
						</IconContainer>
					) : null}
				</Row>
			</FlagsProvider>
		);
	},
);
